import React, { useEffect, useState, useRef } from 'react';
import { ImageIcon, MicIcon, VideoIcon, SendIcon, PlusIcon, InfoIcon, UserIcon } from 'lucide-react';
import { ChatMessage } from '../components/ChatMessage';
import { ModelSelector } from '../components/ModelSelector';
interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}
export function ChatConsole() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState({
    id: 'llama3-8b',
    name: 'Llama 3',
    provider: 'Meta'
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  // Focus input on load
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSendMessage = () => {
    if (inputText.trim() === '') return;
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    // Simulate AI response after a delay
    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getAIResponse(inputText),
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 1000);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const startNewChat = () => {
    setMessages([]);
    inputRef.current?.focus();
  };
  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Mock AI response function
  const getAIResponse = (userInput: string): string => {
    const responses = ["I'm analyzing your request...", "That's an interesting question. Let me think about it.", 'Based on my knowledge, I can help with that.', "I've processed your request and here's what I found.", 'According to my training data, the answer is complex.', "I'm not entirely sure, but here's my best response.", 'Let me provide you with some information on that topic.'];
    return responses[Math.floor(Math.random() * responses.length)];
  };
  return <div className="flex flex-col h-full bg-black text-white" data-id="element-915">
      <div className="flex items-center justify-between border-b border-[#222222] p-4" data-id="element-916">
        <h2 className="text-xl font-bold text-white" data-id="element-917">Chat Console</h2>
        <div className="flex items-center space-x-2" data-id="element-918">
          <ModelSelector selectedModel={selectedModel} onSelectModel={setSelectedModel} data-id="element-919" />
          <button onClick={startNewChat} className="flex items-center px-3 py-2 bg-[#121212] text-gray-300 rounded-md hover:bg-[#1a1a1a] transition-colors" data-id="element-920">
            <PlusIcon size={16} className="mr-1" data-id="element-921" />
            New Chat
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4" data-id="element-922">
        {messages.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-gray-400" data-id="element-923">
            <div className="bg-blue-900/30 rounded-full p-3 mb-3 border border-blue-800" data-id="element-924">
              <InfoIcon size={24} className="text-blue-400" data-id="element-925" />
            </div>
            <p className="text-center" data-id="element-926">Start a conversation with Llama 3</p>
          </div> : messages.map(message => <ChatMessage key={message.id} message={message.text} isUser={message.isUser} timestamp={formatTimestamp(message.timestamp)} data-id="element-927" />)}
        {isLoading && <div className="flex justify-start mb-4" data-id="element-928">
            <div className="flex" data-id="element-929">
              <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-white mr-2" data-id="element-930">
                <UserIcon size={16} data-id="element-931" />
              </div>
              <div className="p-3 bg-[#121212] text-gray-300 rounded-lg rounded-tl-none" data-id="element-932">
                <div className="flex space-x-1" data-id="element-933">
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" data-id="element-934"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-100" data-id="element-935"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-200" data-id="element-936"></div>
                </div>
              </div>
            </div>
          </div>}
        <div ref={messagesEndRef} data-id="element-937" />
      </div>
      <div className="border-t border-[#222222] p-4" data-id="element-938">
        <div className="flex items-center" data-id="element-939">
          <div className="flex space-x-2 mr-2" data-id="element-940">
            <button className="p-2 text-gray-400 hover:text-gray-300 hover:bg-[#1a1a1a] rounded-full transition-colors" data-id="element-941">
              <ImageIcon size={20} data-id="element-942" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-300 hover:bg-[#1a1a1a] rounded-full transition-colors" data-id="element-943">
              <MicIcon size={20} data-id="element-944" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-300 hover:bg-[#1a1a1a] rounded-full transition-colors" data-id="element-945">
              <VideoIcon size={20} data-id="element-946" />
            </button>
          </div>
          <div className="flex-1 relative" data-id="element-947">
            <input ref={inputRef} type="text" value={inputText} onChange={e => setInputText(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your message..." className="w-full border border-[#333333] bg-[#121212] rounded-full py-2 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white" data-id="element-948" />
            <button onClick={handleSendMessage} disabled={inputText.trim() === '' || isLoading} className={`absolute right-1 top-1 p-1.5 rounded-full ${inputText.trim() === '' || isLoading ? 'text-gray-500 bg-[#1a1a1a]' : 'text-white bg-blue-600 hover:bg-blue-700'} transition-colors`} data-id="element-949">
              <SendIcon size={18} data-id="element-950" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}